import type { GetServerSidePropsContext } from "next";
import type { BasePageProps } from "@/types";
import { Fragment, useEffect, useState } from "react";

import { PLACEHOLDER } from "@/components/common/SafeImage";
import LandingCard from "@/components/home/LandingCard";
import { CoverBackground } from "@/components/home/styles";
import { DEFAULT_WEB_COVER } from "@/constants/images";
import { loadBasePageProps } from "@/lib/pageProps";
import { useAppDispatch, useAppSelector } from "@/store";
import { setCloseDates } from "@/store/slices/closeDatesSlice";
import { setRunStoreStatusCheck } from "@/store/slices/commonSlice";
import { closeDrawer, closeModal } from "@/store/slices/commonSlice";
import { setSettings } from "@/store/slices/settingsSlice";
import { setShifts } from "@/store/slices/shiftsSlice";
import { useRouter } from "next/router";
import { ORDER_TYPE_ENUM } from "@/constants/enums";
import { readOrderType, readOrderTypeCookie, orderTypeRoute } from "@/lib/orderingUi";

// `metadata` and `baseUrl` are still returned by `getServerSideProps`: `_app`
// reads them off `pageProps` for `SeoHead`, which the original renders there
// rather than in the page.
export default function HomePage({ data }: BasePageProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const persistedSettings = useAppSelector((state) => state.settings.data);
  // The server payload is authoritative for the first paint. Reading the
  // persisted slice here used to show the previous branch's cover first and
  // swap it after the hydration effect, which looked like repeated blinking.
  const visibleSettings = data?.settings ?? persistedSettings;
  useEffect(() => {
    const saved = readOrderType(visibleSettings._id, isStaff);
    const enabled = saved === ORDER_TYPE_ENUM.DINE_IN
      ? isStaff && visibleSettings.web_enable_dine_in
      : saved === ORDER_TYPE_ENUM.DELIVERY
        ? visibleSettings.web_enable_delivery
        : saved === ORDER_TYPE_ENUM.TAKE_AWAY && visibleSettings.web_enable_takeaway;
    if (saved && enabled) void router.replace(orderTypeRoute(saved, isStaff));
  }, [visibleSettings._id, visibleSettings.web_enable_dine_in, visibleSettings.web_enable_delivery, visibleSettings.web_enable_takeaway, isStaff, router]);
  const {
    web_cover_image: webCoverImage,
    web_slider_images: sliderImages,
  } = visibleSettings;

  // Some recovered tenants still carry the old shared `brand-cover.svg` value.
  // That object contains Butt Karahi artwork, so serving it to another brand
  // leaks the wrong restaurant name across otherwise isolated storefronts.
  // Keep it for the Butt Karahi tenant, but use the neutral local cover for
  // every other brand until that tenant uploads its own web cover in Admin.
  const isLegacyButtKarahiCover = /images%2Fcommon%2Fbrand-cover\.svg/i.test(
    webCoverImage || "",
  );
  const isButtKarahi = /butt\s+karahi/i.test(
    visibleSettings.public_business_name || visibleSettings.name || "",
  );
  const coverImage =
    isLegacyButtKarahiCover && !isButtKarahi
      ? PLACEHOLDER.cover
      : webCoverImage || DEFAULT_WEB_COVER;
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [resolvedCover, setResolvedCover] = useState(DEFAULT_WEB_COVER);

  // Hydrate the store from the server-rendered payload.
  useEffect(() => {
    if (!data) return;
    dispatch(setSettings(data.settings ?? null));
    dispatch(setCloseDates(data.closeDates ?? []));
    dispatch(setShifts(data.shifts ?? []));
    dispatch(setRunStoreStatusCheck(true));
  }, [data, dispatch]);

  // Landing on the home page always resets any open overlay.
  useEffect(() => {
    dispatch(closeDrawer());
    dispatch(closeModal());
  }, [dispatch]);

  // Preload the cover image and only swap it in once it has decoded, so the
  // hero never flashes a half-painted background.
  useEffect(() => {
    if (!coverImage) {
      setCoverLoaded(true);
      return undefined;
    }

    const existing = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>(
        'link[rel="preload"][as="image"]',
      ),
    ).find((link) => link.getAttribute("href") === coverImage);

    const link: HTMLLinkElement = existing ?? document.createElement("link");
    const created = !existing;

    if (!existing) {
      link.rel = "preload";
      link.as = "image";
      link.href = coverImage;
      link.fetchPriority = "high";
      document.head.appendChild(link);
    }

    const image = new Image();
    image.onload = () => {
      setResolvedCover(coverImage);
      setCoverLoaded(true);
    };
    image.onerror = () => {
      // The tenant's cover failed. `DEFAULT_WEB_COVER` lives in the same
      // Firebase bucket, so if that bucket is what is down, retrying it just
      // paints an empty grey hero. Fall back to the locally-served cover.
      if (coverImage !== DEFAULT_WEB_COVER) {
        setResolvedCover(DEFAULT_WEB_COVER);
        const backup = new Image();
        backup.onload = () => setCoverLoaded(true);
        backup.onerror = () => {
          setResolvedCover(PLACEHOLDER.cover);
          setCoverLoaded(true);
        };
        backup.src = DEFAULT_WEB_COVER;
        return;
      }
      setResolvedCover(PLACEHOLDER.cover);
      setCoverLoaded(true);
    };
    image.src = coverImage;

    return () => {
      image.onload = null;
      image.onerror = null;
      if (created && link.parentNode) document.head.removeChild(link);
    };
  }, [coverImage]);

  // Warm the first slider image at low priority once the cover is up.
  useEffect(() => {
    if (!coverLoaded || !sliderImages || sliderImages.length === 0)
      return undefined;

    const first = sliderImages[0];
    if (!first) return undefined;

    const existing = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>(
        'link[rel="preload"][as="image"]',
      ),
    ).find((link) => link.getAttribute("href") === first);

    const link: HTMLLinkElement = existing ?? document.createElement("link");
    const created = !existing;

    if (!existing) {
      link.rel = "preload";
      link.as = "image";
      link.href = first;
      link.fetchPriority = "low";
      document.head.appendChild(link);
    }

    new Image().src = first;

    return () => {
      if (created && link.parentNode) document.head.removeChild(link);
    };
  }, [coverLoaded, sliderImages]);

  return (
    <Fragment>
      <CoverBackground
        // No `component="main"` here: the original's landing hero is a plain
        // Stack, and the page's single `<main>` landmark comes from `_app`.
        className={coverLoaded ? "loaded" : ""}
        sx={{
          backgroundImage: coverLoaded
            ? `url(${resolvedCover})`
            : "linear-gradient(135deg, rgba(25, 25, 25, 0.65) 0%, rgba(35, 35, 35, 0.9) 100%)",
        }}
      >
        <LandingCard />
      </CoverBackground>
    </Fragment>
  );
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<{ props: BasePageProps } | { redirect: { destination: string; permanent: false } }> {
  // The BRANCH is resolved first and both store fetches are made for it, so the
  // server render is already the chosen outlet's. See lib/pageProps.ts.
  const props = await loadBasePageProps(ctx);

  // A returning customer is sent straight to their order type. The effect in the
  // page does the same thing on hydration, but only AFTER the landing page has
  // painted — the visible flash people reported as the site blinking. Dine-in is
  // left to the client: that route is staff-only and the staff session is not
  // readable here.
  const settings = props.data?.settings;
  const saved = readOrderTypeCookie(ctx.req.cookies ?? {}, settings?._id, false);
  const enabled = saved === ORDER_TYPE_ENUM.DELIVERY
    ? settings?.web_enable_delivery
    : saved === ORDER_TYPE_ENUM.TAKE_AWAY && settings?.web_enable_takeaway;
  if (saved && enabled) {
    return { redirect: { destination: orderTypeRoute(saved, false), permanent: false } };
  }
  return { props };
}
